import {
  answer_value,
  first_truthy,
  form_questions,
  is_document_answer,
  is_submitter_question,
  is_what_happened_question,
  normalize_text,
  object_field,
  question_label,
  submitted_answer_records,
  to_number,
} from './view_case_details_core_helpers';

const DOCUMENT_URL_FIELDS = ['presigned_url', 'preview_url', 'url', 'document_url', 'file_url'];
const DOCUMENT_NAME_FIELDS = ['file_name', 'document_name', 'name', 'title', 'question'];
const DOCUMENT_DATE_FIELDS = ['submitted_on', 'date_uploaded', 'created_at', 'date_created'];
const DOCUMENT_SIZE_FIELDS = ['file_size', 'size', 'content_length'];

function field_values(source = {}, fields = []) {
  return fields.map((field) => object_field(source, field));
}

export function submitted_case_context_parts(application_forms = [], excluded_values = []) {
  const excluded = new Set(excluded_values.map((value) => normalize_text(value).toLowerCase()).filter(Boolean));
  const seen = new Set();

  return application_forms
    .flatMap((application_form) => [
      ...submitted_answer_records(application_form),
      ...form_questions(application_form),
    ])
    .filter(context_question_visible)
    .map(context_item)
    .filter((item) => context_item_allowed(item, excluded))
    .filter((item) => unique_context_item(item, seen))
    .map((item) => item.label ? `${item.label}: ${item.value}` : item.value)
    .slice(0, 4);
}

function context_question_visible(question) {
  return !is_what_happened_question(question) && !is_submitter_question(question) && !is_document_answer(question);
}

function context_item(question) {
  return {
    label: question_label(question),
    value: answer_value(question),
  };
}

function context_item_allowed(item, excluded) {
  return item.value && !excluded.has(item.value.toLowerCase());
}

function unique_context_item(item, seen) {
  const key = `${item.label}:${item.value}`.toLowerCase();
  if (seen.has(key)) return false;
  seen.add(key);
  return true;
}

export function extension_from_name(value = '') {
  const clean_value = normalize_text(value).split('?')[0];
  const parts = clean_value.split('.');

  return parts.length > 1 ? parts.pop().toUpperCase() : 'FILE';
}

export function format_file_size(value) {
  const size = Number(value);
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;

  const decimals = size >= 10 * 1024 * 1024 ? 0 : 1;
  return `${(size / (1024 * 1024)).toFixed(decimals)} MB`;
}

export function format_document_date(value) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return normalize_text(value);

  return date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function filename_from_url(value = '') {
  const url = normalize_text(value).split('?')[0];
  const parts = url.split('/').filter(Boolean);

  return decodeURIComponent(parts.pop() || '');
}

export function document_url(document = {}) {
  return normalize_text(first_truthy(field_values(document, DOCUMENT_URL_FIELDS)));
}

export function normalize_document(document = {}, index = 0) {
  const url = document_url(document);
  const name = document_name(document, url, index);
  const extension = document_extension(document, name, url);
  const verification_state = normalize_verification_state(document);
  const verification_label = verification_state === 'verified'
    ? 'Verified'
    : verification_state === 'rejected'
      ? 'Rejected'
      : 'Pending review';

  return {
    ...document,
    id: document?.id || `${name}-${index}`,
    additional_document_id: to_number(document?.additional_document_id || document?.id),
    name,
    meta: normalize_text(document?.meta) || uploaded_label(document),
    extension,
    size: format_file_size(first_truthy(field_values(document, DOCUMENT_SIZE_FIELDS))),
    url,
    is_verified: document?.is_verified,
    verification_comment: normalize_text(document?.verification_comment),
    verification_state,
    verification_label,
  };
}

function normalize_verification_state(document = {}) {
  const explicit_state = normalize_text(document?.verification_state).toLowerCase();
  if (['verified', 'rejected', 'pending'].includes(explicit_state)) {
    return explicit_state;
  }

  if (document?.is_verified === true) {
    return 'verified';
  }

  if (document?.verified_by_id != null) {
    return 'rejected';
  }

  return 'pending';
}

function document_name(document, url, index) {
  return normalize_text(first_truthy([
    ...field_values(document, DOCUMENT_NAME_FIELDS),
    filename_from_url(url),
    `Document ${index + 1}`,
  ]));
}

function document_extension(document, name, url) {
  const extension = normalize_text(document?.extension || document?.file_type || extension_from_name(name || url));
  const fallback_extension = normalize_text(document?.meta).split(/[\s-]+/)[0];
  return extension === 'FILE' && fallback_extension ? fallback_extension.toUpperCase() : extension;
}

function uploaded_label(document) {
  return [
    uploader_label(document),
    format_document_date(first_truthy(field_values(document, DOCUMENT_DATE_FIELDS))),
  ].filter(Boolean).join(' - ');
}

function uploader_label(document) {
  const source = normalize_text(document?.source);
  const uploaded_by = normalize_text(first_truthy(field_values(document, [
    'uploaded_by',
    'uploader_name',
    'uploader',
    'created_by_name',
  ])));

  if (uploaded_by) return `Uploaded by ${uploaded_by}`;
  return source ? `Uploaded from ${source}` : 'Uploaded document';
}
